import { EventEmitter, Component, Element, Event, h, Host, Prop } from '@stencil/core';

import { ChipTextAlignPreset, ChipTypesPreset, ChipVariantsPreset } from '../../../services/interfaces';
import { isNullUndefined } from '../../../services/utils';

/**
 * Chips are compact elements that represent an input, attribute, or action.
 *
 * @part button - HTML `<button>` element used under the hood
 * @part icon - HTML `<span>` element that holds the pk-icon provided
 * @part icon-close - HTMLPkIconElement `<pk-icon>` shown when the Chip type="filter"
 * @part text - HTML `<div>` element that group the primary and secondary text
 * @part text-primary - HTML `<span>` element that holds the primary text
 * @part text-secondary - HTML `<span>` element that holds the secondary text
 */
@Component({
  tag: 'pk-chip',
  styleUrl: './scss/pk-chip.scss',
  shadow: {
    delegatesFocus: true,
  },
})
export class PkChip {
  // Own Properties
  // ====================

  // Reference to host HTML element
  // ===================================

  @Element() el!: HTMLPkChipElement;

  // State() variables
  // Inlined decorator, alphabetical order
  // =======================================

  // Public Property API
  // ========================

  /** If true, the component will enbale the selected/active state */
  @Prop({ reflect: true, mutable: true }) selected: boolean = false;

  /** Set the alignment of the primary and secondary text */
  @Prop({ mutable: true }) textAlign: ChipTextAlignPreset = 'center';

  /**
   * It defines how the component should be used
   * (a "navigation-close" pk-icon will be shown when used as `filter`)
   */
  @Prop({ reflect: true }) type: ChipTypesPreset = 'default';

  /** The href of the link. If is empty, a `<button>` HTML element will be used instead */
  @Prop({ reflect: true }) linkHref?: string;

  /**
   * Specifies where to display the linked URL, as the name for a browsing context (a tab, window, or <iframe>).
   * Details: https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target
   */
  @Prop({ reflect: true }) linkTarget?: string;

  /** Specifies the relationship between the current page and the linked page */
  @Prop({ reflect: true }) linkRel?: string;

  /** If true, the `selected` state will be toggled when the Chip is clicked */
  @Prop({ reflect: true }) toggleSelectOnClick: boolean = false;

  /**
   * The variant style to apply
   * (please, keep in mind that each variant have their own styles and behaviour)
   */
  @Prop({ reflect: true }) variant: ChipVariantsPreset = 'stroke';

  /** I sets the component width in px */
  @Prop({ reflect: true }) width?: string;

  // Prop lifecycle events
  // =======================

  // Events section
  // Requires JSDocs for public API documentation
  // ==============================================

  /** Callback handler to be called when the Chip is clicked */
  @Event() pkClick: EventEmitter;

  // Component lifecycle events
  // Ordered by their natural call order
  // =====================================

  // Listeners
  // ==============

  // Public methods API
  // These methods are exposed on the host element.
  // Always use two lines.
  // Public Methods must be async.
  // Requires JSDocs for public API documentation.
  // ===============================================

  // Local methods
  // Internal business logic.
  // These methods cannot be called from the host element.
  // =======================================================

  private handleClick = () => {
    if (this.toggleSelectOnClick && this.type !== 'filter') {
      this.toggleSelected();
    }

    this.pkClick.emit(this.el);
  };

  private toggleSelected = () => (this.selected = !this.selected);

  private get isLink(): boolean {
    return !!this.linkHref;
  }

  // render() function
  // Always the last one in the class.
  // ===================================

  render() {
    const hostStyle = {
      ...(this.textAlign && { '--pk-chip--text-align': this.textAlign }),
      ...(this.width && { '--pk-chip--width': `${this.width}px` }),
    };

    const buttonCssClasses = {
      'pk-chip--button': true,
      [`pk-chip--button__${this.variant}`]: true,
      selected: this.selected,
      fixed: !isNullUndefined(this.width),
    };

    const TagElem = this.isLink ? 'a' : 'button';

    return (
      <Host style={hostStyle}>
        <TagElem
          href={this.isLink ? this.linkHref : undefined}
          rel={this.isLink ? this.linkRel : undefined}
          target={this.isLink ? this.linkTarget : undefined}
          type={this.isLink ? undefined : 'button'}
          part={this.isLink ? 'link' : 'button'}
          class={buttonCssClasses}
          onClick={this.handleClick}
        >
          <div class="relative flex flex-col" part="text">
            <span
              class="font-regular text-m leading-m text-[color:var(--chip--primary-color)] transition duration-250"
              part="text-primary"
            >
              <slot />
            </span>
            <span
              class="font-regular text-s leading-s text-[color:var(--chip--secondary-color)] transition duration-250"
              part="text-secondary"
            >
              <slot name="text-secondary" />
            </span>
          </div>
          <span class="relative text-[color:var(--chip--primary-color)]" part="icon">
            {this.type === 'default' && <slot name="icon" />}
            {this.type === 'filter' && (
              <pk-icon
                class="icon-close"
                name="navigation-close"
                size={20}
                role="img"
                aria-label="Close filter"
                part="icon-close"
              />
            )}
          </span>
        </TagElem>
      </Host>
    );
  }
}
